import React, { useState, useEffect } from 'react';
import { useInfluencerAuth } from '../../contexts/InfluencerAuthContext';
import { influencerAPI } from '../../services/influencerApi';
import { Card, Input, Select, Button, Skeleton } from '../../components/ui';
import {
  Person,
  Pix,
  Save,
  CheckCircle,
  Warning,
} from '../../mui-icons';
import './InfluencerProfilePage.css';

const InfluencerProfilePage = () => {
  const { updateProfile } = useInfluencerAuth();

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    phone: '',
    pix_key_type: '',
    pix_key: '',
    bank_name: '',
    bank_agency: '',
    bank_account: '',
    bank_account_type: '',
  });

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const response = await influencerAPI.getMe();
        if (response.status === 'success') {
          setProfile(response.data);
          setFormData({
            phone: response.data.phone || '',
            pix_key_type: response.data.payment_info?.pix_key_type || '',
            pix_key: response.data.payment_info?.pix_key || '',
            bank_name: response.data.payment_info?.bank_name || '',
            bank_agency: response.data.payment_info?.bank_agency || '',
            bank_account: response.data.payment_info?.bank_account || '',
            bank_account_type: response.data.payment_info?.bank_account_type || '',
          });
        }
      } catch (err) {
        console.error('Erro ao carregar perfil:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);

    if (formData.pix_key_type && !formData.pix_key) {
      setError('Se selecionar tipo de chave PIX, informe a chave');
      setSaving(false);
      return;
    }

    try {
      const updateData = {};

      if (formData.phone !== profile.phone) {
        updateData.phone = formData.phone || null;
      }
      if (formData.pix_key_type !== profile.payment_info?.pix_key_type) {
        updateData.pix_key_type = formData.pix_key_type || null;
      }
      if (formData.pix_key !== profile.payment_info?.pix_key) {
        updateData.pix_key = formData.pix_key || null;
      }
      if (formData.bank_name !== profile.payment_info?.bank_name) {
        updateData.bank_name = formData.bank_name || null;
      }
      if (formData.bank_agency !== profile.payment_info?.bank_agency) {
        updateData.bank_agency = formData.bank_agency || null;
      }
      if (formData.bank_account !== profile.payment_info?.bank_account) {
        updateData.bank_account = formData.bank_account || null;
      }
      if (formData.bank_account_type !== profile.payment_info?.bank_account_type) {
        updateData.bank_account_type = formData.bank_account_type || null;
      }

      if (Object.keys(updateData).length === 0) {
        setSuccess('Nenhuma alteração detectada');
        setSaving(false);
        return;
      }

      await updateProfile(updateData);
      setSuccess('Dados atualizados com sucesso!');

      const response = await influencerAPI.getMe();
      if (response.status === 'success') {
        setProfile(response.data);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('pt-BR');
  };

  if (loading) {
    return (
      <div className="influencer-profile-page">
        <Skeleton width={200} height={28} borderRadius={4} />
        <Skeleton width={280} height={16} borderRadius={4} style={{ marginTop: 8, marginBottom: 24 }} />
        <Card variant="default" className="influencer-profile-skeleton-card">
          <Skeleton width="60%" height={20} borderRadius={4} />
          <div className="influencer-profile-skeleton-grid">
            {[1, 2, 3, 4].map((i) => (
              <div key={i}>
                <Skeleton width={80} height={12} borderRadius={4} />
                <Skeleton width="90%" height={16} borderRadius={4} style={{ marginTop: 8 }} />
              </div>
            ))}
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="influencer-profile-page">
      <h1>Meu Perfil</h1>
      <p className="page-subtitle">Gerencie seus dados pessoais e de pagamento</p>

      <Card
        variant="default"
        title={
          <span className="influencer-profile-card-title">
            <Person /> Informações Pessoais
          </span>
        }
        className="influencer-profile-info-card"
      >
        <div className="info-grid">
          <div className="info-item">
            <label>Nome</label>
            <span>{profile?.name || '-'}</span>
          </div>
          <div className="info-item">
            <label>Email</label>
            <span>{profile?.email || '-'}</span>
          </div>
          <div className="info-item">
            <label>Documento</label>
            <span>{profile?.document || '-'}</span>
          </div>
          <div className="info-item">
            <label>Cadastrado em</label>
            <span>{formatDate(profile?.created_at)}</span>
          </div>
        </div>
      </Card>

      <Card
        variant="default"
        title={
          <span className="influencer-profile-card-title">
            <Pix /> Dados de Pagamento
          </span>
        }
        className="influencer-profile-form-card"
      >
        <form onSubmit={handleSubmit} className="influencer-profile-form">
          {error && (
            <div className="form-message error">
              <Warning sx={{ fontSize: 20 }} />
              {error}
            </div>
          )}

          {success && (
            <div className="form-message success">
              <CheckCircle sx={{ fontSize: 20 }} />
              {success}
            </div>
          )}

          <Input
            label="Telefone"
            type="tel"
            id="phone"
            name="phone"
            icon="phone"
            placeholder="+55 11 99999-9999"
            value={formData.phone}
            onChange={handleChange}
            disabled={saving}
          />

          <div className="form-section">
            <h3>Chave PIX</h3>
            <div className="form-row">
              <Select
                label="Tipo de Chave"
                id="pix_key_type"
                name="pix_key_type"
                value={formData.pix_key_type}
                onChange={handleChange}
                disabled={saving}
              >
                <option value="">Selecione...</option>
                <option value="cpf">CPF</option>
                <option value="cnpj">CNPJ</option>
                <option value="email">Email</option>
                <option value="telefone">Telefone</option>
                <option value="aleatoria">Chave Aleatória</option>
              </Select>
              <Input
                label="Chave PIX"
                type="text"
                id="pix_key"
                name="pix_key"
                icon="cash"
                placeholder="Sua chave PIX"
                value={formData.pix_key}
                onChange={handleChange}
                disabled={saving}
              />
            </div>
          </div>

          <div className="form-section">
            <h3>Dados Bancários (opcional)</h3>
            <div className="form-row">
              <Input
                label="Banco"
                type="text"
                id="bank_name"
                name="bank_name"
                placeholder="Nome do banco"
                value={formData.bank_name}
                onChange={handleChange}
                disabled={saving}
              />
              <Select
                label="Tipo de Conta"
                id="bank_account_type"
                name="bank_account_type"
                value={formData.bank_account_type}
                onChange={handleChange}
                disabled={saving}
              >
                <option value="">Selecione...</option>
                <option value="corrente">Conta Corrente</option>
                <option value="poupanca">Conta Poupança</option>
              </Select>
            </div>
            <div className="form-row">
              <Input
                label="Agência"
                type="text"
                id="bank_agency"
                name="bank_agency"
                placeholder="0000"
                value={formData.bank_agency}
                onChange={handleChange}
                disabled={saving}
              />
              <Input
                label="Conta"
                type="text"
                id="bank_account"
                name="bank_account"
                placeholder="00000-0"
                value={formData.bank_account}
                onChange={handleChange}
                disabled={saving}
              />
            </div>
          </div>

          <div className="form-actions">
            <Button type="submit" variant="primary" loading={saving} block>
              <Save sx={{ fontSize: 20 }} />
              Salvar Alterações
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default InfluencerProfilePage;
